import React, { createContext, useCallback, useContext, useLayoutEffect, useRef, useState } from 'react';
import {
  EdgeLabelRenderer,
  EdgeProps,
  getBezierPath,
  getSmoothStepPath,
  getStraightPath,
  useReactFlow,
} from 'reactflow';
import { EdgeData } from '../../types';
import { Tooltip } from '@/components/ui/beui';

interface EdgeLabelContextValue {
  /** Labels are only draggable in the editor, not in read-only viewers. */
  editable: boolean;
  /** Called once at drag start so undo returns to the pre-drag position. */
  onSnapshot?: () => void;
}

const EdgeLabelContext = createContext<EdgeLabelContextValue>({ editable: false });

export function EdgeLabelProvider({
  editable,
  onSnapshot,
  children,
}: EdgeLabelContextValue & { children: React.ReactNode }) {
  const value = React.useMemo(() => ({ editable, onSnapshot }), [editable, onSnapshot]);
  return <EdgeLabelContext.Provider value={value}>{children}</EdgeLabelContext.Provider>;
}

/** Every path helper returns [path, labelX, labelY, offsetX, offsetY]. */
type PathResult = [string, number, number, number, number];
type PathFn = (props: EdgeProps) => PathResult;

const bezier: PathFn = (p) =>
  getBezierPath({
    sourceX: p.sourceX, sourceY: p.sourceY, sourcePosition: p.sourcePosition,
    targetX: p.targetX, targetY: p.targetY, targetPosition: p.targetPosition,
  });

const smoothstep: PathFn = (p) =>
  getSmoothStepPath({
    sourceX: p.sourceX, sourceY: p.sourceY, sourcePosition: p.sourcePosition,
    targetX: p.targetX, targetY: p.targetY, targetPosition: p.targetPosition,
  });

const step: PathFn = (p) =>
  getSmoothStepPath({
    sourceX: p.sourceX, sourceY: p.sourceY, sourcePosition: p.sourcePosition,
    targetX: p.targetX, targetY: p.targetY, targetPosition: p.targetPosition,
    borderRadius: 0,
  });

const straight: PathFn = (p) =>
  getStraightPath({ sourceX: p.sourceX, sourceY: p.sourceY, targetX: p.targetX, targetY: p.targetY });

/** Midpoint of the path. Matches React Flow's default label placement. */
const DEFAULT_T = 0.5;

/**
 * Keep the label clear of both endpoints. Parked exactly on a handle it sits
 * under the node, which paints over it — leaving it unreachable for a second
 * drag. Flow units; shrinks on short edges so there's always somewhere to go.
 */
const END_MARGIN = 30;

/** Clamp a position along the path to the draggable span. */
function clampT(t: number, total: number): number {
  if (total === 0) return DEFAULT_T;
  const margin = Math.min(END_MARGIN, total * 0.2);
  const lo = margin / total;
  const hi = 1 - lo;
  if (lo >= hi) return DEFAULT_T; // edge too short to offer a range
  return Math.min(hi, Math.max(lo, t));
}

/**
 * Position along `path`, as a fraction of its length, closest to `point`.
 * Coarse sweep to find the neighbourhood, then bisect twice to refine — plenty
 * for a label the user is dragging by hand, and cheap enough to run per frame.
 */
function nearestT(path: SVGPathElement, point: { x: number; y: number }): number {
  const total = path.getTotalLength();
  if (total === 0) return DEFAULT_T;

  const dist2 = (len: number) => {
    const p = path.getPointAtLength(len);
    return (p.x - point.x) ** 2 + (p.y - point.y) ** 2;
  };

  const SAMPLES = 100;
  let best = 0;
  let bestD = Infinity;
  for (let i = 0; i <= SAMPLES; i++) {
    const len = (i / SAMPLES) * total;
    const d = dist2(len);
    if (d < bestD) {
      bestD = d;
      best = len;
    }
  }

  let window = total / SAMPLES;
  for (let pass = 0; pass < 2; pass++) {
    const lo = Math.max(0, best - window);
    const hi = Math.min(total, best + window);
    const mid = [lo, (lo + best) / 2, best, (best + hi) / 2, hi];
    for (const len of mid) {
      const d = dist2(len);
      if (d < bestD) {
        bestD = d;
        best = len;
      }
    }
    window /= 4;
  }

  return clampT(best / total, total);
}

function EdgeWithMovableLabel({ getPath, ...props }: EdgeProps<EdgeData> & { getPath: PathFn }) {
  const { id, label, style, markerEnd, markerStart, interactionWidth = 20, data, selected } = props;
  const { editable, onSnapshot } = useContext(EdgeLabelContext);
  const { setEdges, screenToFlowPosition } = useReactFlow();

  const [path, midX, midY] = getPath(props);
  const t = data?.labelT ?? DEFAULT_T;
  const color = data?.labelColor;

  // The rendered path is the source of truth for geometry: getPointAtLength
  // handles beziers and rounded corners that we'd otherwise have to solve.
  const pathRef = useRef<SVGPathElement>(null);
  const [labelPos, setLabelPos] = useState({ x: midX, y: midY });

  useLayoutEffect(() => {
    const el = pathRef.current;
    if (!el) return;
    const total = el.getTotalLength();
    // Clamp on read too: the edge may have been reshaped (or the value saved by
    // an older build) since the label was placed.
    const p = el.getPointAtLength(clampT(t, total) * total);
    setLabelPos({ x: p.x, y: p.y });
  }, [path, t]);

  const dragging = useRef(false);
  // EdgeLabelRenderer portals the label out of the edge's <g>, but React events
  // still bubble up the React tree to the edge's onClick. Clicking a label to
  // select its edge is fine; finishing a drag on it is not.
  const moved = useRef(false);
  const [isDragging, setIsDragging] = useState(false);

  const setT = useCallback(
    (next: number | undefined) => {
      setEdges((edges) => edges.map((e) => (e.id === id ? { ...e, data: { ...e.data, labelT: next } } : e)));
    },
    [id, setEdges]
  );

  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!editable) return;
    e.stopPropagation(); // don't let the canvas start a selection box
    e.currentTarget.setPointerCapture(e.pointerId);
    dragging.current = true;
    moved.current = false;
    setIsDragging(true);
    onSnapshot?.(); // one snapshot per drag, not per pointermove
  };

  const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!dragging.current || !pathRef.current) return;
    moved.current = true;
    // Project the cursor onto the path — the label can only travel along it.
    const cursor = screenToFlowPosition({ x: e.clientX, y: e.clientY });
    setT(nearestT(pathRef.current, cursor));
  };

  const endDrag = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!dragging.current) return;
    e.currentTarget.releasePointerCapture(e.pointerId);
    dragging.current = false;
    setIsDragging(false);
  };

  const onClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (moved.current) e.stopPropagation(); // don't select the edge after a drag
    moved.current = false;
  };

  const recentre = () => {
    if (!editable || data?.labelT === undefined) return;
    onSnapshot?.();
    setT(undefined);
  };

  return (
    <>
      {/* Mirrors BaseEdge, but keeps a ref so the label can be projected onto
          the path. Same classNames, so the animated-dash CSS still applies. */}
      <path
        ref={pathRef}
        id={id}
        d={path}
        style={style}
        fill="none"
        className="react-flow__edge-path"
        markerEnd={markerEnd}
        markerStart={markerStart}
      />
      {interactionWidth ? (
        <path
          d={path}
          fill="none"
          strokeOpacity={0}
          strokeWidth={interactionWidth}
          className="react-flow__edge-interaction"
        />
      ) : null}

      {label && (
        <EdgeLabelRenderer>
          <div
            // nodrag/nopan stop the canvas panning while the label is dragged.
            className={`nodrag nopan absolute rounded-md border px-1.5 py-0.5 text-[11px] font-medium shadow-sm transition-colors ${
              // A custom colour is applied inline below, so only fall back to the
              // token classes when there isn't one. Selection always wins.
              selected ? 'border-primary text-foreground' : color ? '' : 'border-border text-muted-foreground'
            } ${editable ? (isDragging ? 'cursor-grabbing' : 'cursor-grab') : ''}`}
            style={{
              transform: `translate(-50%, -50%) translate(${labelPos.x}px, ${labelPos.y}px)`,
              pointerEvents: 'all', // EdgeLabelRenderer's container disables them
              background: 'var(--background)',
              ...(color && {
                color,
                // Tint the border rather than matching it, or the pill shouts.
                ...(selected ? {} : { borderColor: `color-mix(in oklab, ${color} 55%, transparent)` }),
              }),
            }}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={endDrag}
            onPointerCancel={endDrag}
            onClick={onClick}
            onDoubleClick={recentre}
          >
            {/* The tooltip wraps the text, not the pill: the pill is absolutely
                positioned by inline transform inside EdgeLabelRenderer, and the
                Tooltip's relative anchor span would become its containing block. */}
            {editable ? (
              <Tooltip content="Drag along the line · double-click to recentre">
                <span>{label}</span>
              </Tooltip>
            ) : (
              label
            )}
          </div>
        </EdgeLabelRenderer>
      )}
    </>
  );
}

const withPath = (getPath: PathFn) => {
  const Edge = (props: EdgeProps<EdgeData>) => <EdgeWithMovableLabel {...props} getPath={getPath} />;
  return Edge;
};

/**
 * Overrides React Flow's built-in edge types by name, so stored `edge.type`
 * values ('smoothstep' | 'default' | 'step' | 'straight') keep working and no
 * diagram needs migrating.
 *
 * Note: createEdgeTypes() in reactflow@11.11.4 wires `straight` to
 * `edgeTypes.bezier` and `smoothstep` to `edgeTypes.step`. Those fallbacks only
 * apply when a key is absent — keys we pass land in `specialTypes`, which is
 * spread last and wins. Supply all four or the mapping goes wrong.
 */
export const EDGE_TYPES = {
  default: withPath(bezier),
  smoothstep: withPath(smoothstep),
  step: withPath(step),
  straight: withPath(straight),
};
